import { useState } from "react";
import { Link } from "react-router";
import {
  Building2,
  User,
  Phone,
  Users,
  Send,
  CheckCircle2,
  Tablet,
  ChevronLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRealEstateContext } from "../providers/RealEstateContext";

export const WalkInKiosk = () => {
  const { addVisitorFromKiosk, projects, channelPartners } =
    useRealEstateContext();
  const [visitorName, setVisitorName] = useState("");
  const [visitorPhone, setVisitorPhone] = useState("");
  const [visitType, setVisitType] = useState<"direct" | "cp">("direct");
  const [selectedCp, setSelectedCp] = useState<string>(
    channelPartners[0]?.id || "",
  );
  const [targetProject, setTargetProject] = useState<string>(
    projects[0]?.id || "",
  );
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assignedAgent, setAssignedAgent] = useState("Sales Desk");
  const [lockNote, setLockNote] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const result = await addVisitorFromKiosk(
        visitorName,
        visitorPhone,
        visitType,
        visitType === "cp" ? selectedCp || undefined : undefined,
        targetProject || undefined,
      );
      if (!result.ok) {
        setFormError(result.error || "Check-in failed");
        return;
      }
      setAssignedAgent(result.assignedAgentName);
      setLockNote(
        result.lockRetained
          ? `30-day Channel Partner lock kept the original broker attribution${
              result.cpLockedUntil
                ? ` until ${new Date(result.cpLockedUntil).toLocaleDateString("en-IN")}`
                : ""
            }.`
          : visitType === "cp"
            ? "30-day Channel Partner lead lock applied to this visitor."
            : null,
      );
      setIsCheckedIn(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setVisitorName("");
    setVisitorPhone("");
    setVisitType("direct");
    setIsCheckedIn(false);
    setLockNote(null);
    setFormError(null);
    setAssignedAgent("Sales Desk");
  };

  return (
    <div className="relative min-h-screen bg-background p-6 flex flex-col justify-center items-center max-w-4xl mx-auto select-none">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="absolute top-4 left-4 z-10 text-muted-foreground hover:text-foreground"
      >
        <Link to="/">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to CRM
        </Link>
      </Button>

      <div className="text-center space-y-2 mb-6">
        <Badge
          variant="outline"
          className="text-xs px-3 py-1 bg-primary/10 text-primary border-primary/20"
        >
          <Tablet className="w-3.5 h-3.5 mr-1.5" /> Site Office Touchscreen
          Reception Kiosk
        </Badge>
        <h1 className="text-3xl font-extrabold text-foreground tracking-tight">
          Welcome to Site Experience Center
        </h1>
        <p className="text-sm text-muted-foreground">
          Please check-in to meet your assigned sales executive & receive an
          instant WhatsApp project pass.
        </p>
      </div>

      {isCheckedIn ? (
        <Card className="w-full border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20 text-center p-8 space-y-4">
          <div className="mx-auto w-16 h-16 bg-emerald-600 text-white rounded-full flex items-center justify-center shadow-lg">
            <CheckCircle2 className="w-10 h-10" />
          </div>

          <div className="space-y-1">
            <h2 className="text-2xl font-bold text-emerald-900 dark:text-emerald-300">
              Welcome, {visitorName}!
            </h2>
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              Your check-in is confirmed. Sales Executive{" "}
              <strong>{assignedAgent}</strong> is arriving at reception to greet
              you.
            </p>
          </div>

          <div className="bg-background p-4 rounded-xl border max-w-md mx-auto text-xs space-y-1.5 text-left">
            <p className="text-muted-foreground">Check-in Details:</p>
            <p className="font-semibold text-foreground">
              Phone: {visitorPhone}
            </p>
            <p className="font-semibold text-foreground">
              Visit Type:{" "}
              {visitType === "cp"
                ? "Accompanied by Channel Partner"
                : "Direct Walk-In"}
            </p>
            {lockNote && (
              <p className="text-amber-700 dark:text-amber-400 font-medium pt-1">
                {lockNote}
              </p>
            )}
            <p className="text-emerald-600 dark:text-emerald-400 flex items-center font-medium pt-1">
              <Send className="w-3.5 h-3.5 mr-1" /> WhatsApp Welcome Pass sent
              to {visitorPhone}
            </p>
          </div>

          <Button
            onClick={handleReset}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Register Next Visitor
          </Button>
        </Card>
      ) : (
        <Card className="w-full shadow-lg border-muted">
          <CardHeader className="border-b bg-muted/20">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Visitor Check-In Registration
            </CardTitle>
            <CardDescription>
              Enter buyer contact info for instant sales executive matching.
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleCheckIn}>
            <CardContent className="space-y-5 pt-6 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-primary" /> Full Name
                  </label>
                  <Input
                    placeholder="e.g. Rahul Sharma"
                    value={visitorName}
                    onChange={(e) => setVisitorName(e.target.value)}
                    className="h-11"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-primary" /> 10-Digit
                    Mobile Number
                  </label>
                  <Input
                    placeholder="+91 98765 12345"
                    value={visitorPhone}
                    onChange={(e) => setVisitorPhone(e.target.value)}
                    className="h-11"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">
                  Visit Accompaniment
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={visitType === "direct" ? "default" : "outline"}
                    className={`h-12 text-xs justify-center ${visitType === "direct" ? "bg-primary text-primary-foreground" : ""}`}
                    onClick={() => setVisitType("direct")}
                  >
                    Direct Walk-In
                  </Button>
                  <Button
                    type="button"
                    variant={visitType === "cp" ? "default" : "outline"}
                    className={`h-12 text-xs justify-center ${visitType === "cp" ? "bg-primary text-primary-foreground" : ""}`}
                    onClick={() => setVisitType("cp")}
                  >
                    <Users className="w-4 h-4 mr-1.5" /> Accompanied by Channel
                    Partner
                  </Button>
                </div>
              </div>

              {visitType === "cp" && (
                <div className="space-y-1.5 bg-muted/40 p-3 rounded-lg border">
                  <label className="text-xs font-semibold text-foreground">
                    Select Channel Partner / Broker Agency
                  </label>
                  <Select value={selectedCp} onValueChange={setSelectedCp}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose Agency" />
                    </SelectTrigger>
                    <SelectContent>
                      {channelPartners.map((cp) => (
                        <SelectItem key={cp.id} value={cp.id}>
                          {cp.agency_name} ({cp.contact_person} - {cp.phone})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground pt-1">
                    Selecting a broker starts a 30-day lead lock. If this phone
                    is already locked to another CP, that attribution is kept.
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-primary" /> Target
                  Project
                </label>
                <Select value={targetProject} onValueChange={setTargetProject}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select Project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((proj) => (
                      <SelectItem key={proj.id} value={proj.id}>
                        {proj.name} ({proj.location_address}, {proj.city})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formError && (
                <p className="text-sm text-destructive font-medium">
                  {formError}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting || (visitType === "cp" && !selectedCp)}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base shadow-md"
              >
                {submitting ? "Checking in…" : "Check In & Meet Sales Manager"}
              </Button>
            </CardContent>
          </form>
        </Card>
      )}
    </div>
  );
};
